import type { FunctionComponent } from 'react';
import { useRef } from 'react';

import { Tooltip, type TooltipProps } from './Tooltip';

export interface WithTooltipProps {
  tooltipOptions: Omit<TooltipProps, 'targetRef'>;
}

/**
 * This HOC can be used to get a wrapped component with a tooltip.
 * It expects that forwardRef will be assigned to the element that needs to trigger the tooltip.
 * @param Component Component with forwardRef that should have a tooltip.
 * @returns Extetended component that has tooltipOptions prop which must be provided for the tooltip to be displayed correctly.
 */
export function withTooltip<P>(Component: FunctionComponent<P>): FunctionComponent<P & WithTooltipProps> {
  const ExtendedComponent: FunctionComponent<P & WithTooltipProps> = (props) => {
    const targetRef = useRef<HTMLDivElement | null>(null);
    const { tooltipOptions } = props;
    return (
      <Tooltip targetRef={targetRef} {...tooltipOptions}>
        <Component {...props} ref={targetRef} />
      </Tooltip>
    );
  };

  return ExtendedComponent;
}
